import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { AppConfigService } from './appconfig.service';

export class AuthFactories {
    public initializerFactory(config: AppConfigService) {
        return () => config.init();
    }

    public MSALInstanceFactory(config: AppConfigService): IPublicClientApplication {
        return new PublicClientApplication({
            auth: {
                clientId: config.getConfig("clientId"),
                authority: `https://login.microsoftonline.com/${config.getConfig("tenantId")}`,
                redirectUri: config.getConfig('redirectUri') ?? '/',
                postLogoutRedirectUri: config.getConfig('redirectUri') ?? '/'
            },
            cache: {
                cacheLocation: BrowserCacheLocation.SessionStorage
            }
        });
    }

    public MSALGuardConfigFactory(config: AppConfigService): MsalGuardConfiguration {
        return {
            interactionType: InteractionType.Redirect,
            authRequest: {
                scopes: [`${config.getConfig("clientId")}/.default`]
            },
            loginFailedRoute: '/'
        };
    }

    public MSALInterceptorConfigFactory(config: AppConfigService): MsalInterceptorConfiguration {
        const protectedResourceMap = new Map<string, Array<string>>();
        const apiUrl = config.getConfig("apiUrl");
        const clientId = config.getConfig("clientId");
        const enabled = config.getConfig("msalEnabled");
        const legacyEnabled = config.getConfig("msalEnable");

        if (apiUrl && clientId && Boolean(enabled ?? legacyEnabled)) {
            protectedResourceMap.set(`${apiUrl}/*`, [`${clientId}/.default`]);
        }

        return {
            interactionType: InteractionType.Popup,
            protectedResourceMap
        };
    }

    public MSALInstanceSetActiveAccountFactory(config: AppConfigService, msalInstance: IPublicClientApplication) {
        const storeToken = (accessToken: string, expiresOn?: Date | null): void => {
            sessionStorage.setItem('access_token', accessToken);
            if (expiresOn) {
                sessionStorage.setItem('access_token_expires_on', expiresOn.toISOString());
            }
        };

        return async () => {
            await config.init();
            if (config.getConfig('msalEnable') === false) {
                return;
            }
            await msalInstance.initialize();

            const result = await msalInstance.handleRedirectPromise();
            const clientId = config.getConfig('clientId');
            const scopes = [`${clientId}/.default`];

            if (result?.account) {
                msalInstance.setActiveAccount(result.account);
                storeToken(result.accessToken, result.expiresOn);
            } else {
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            }

            const activeAccount = msalInstance.getActiveAccount();

            if (activeAccount && !sessionStorage.getItem('access_token')) {
                try {
                    const tokenResult = await msalInstance.acquireTokenSilent({
                        account: activeAccount,
                        scopes
                    });
                    storeToken(tokenResult.accessToken, tokenResult.expiresOn);
                } catch (err) {
                    console.warn('[MSAL Init] Silent token acquisition failed', err);
                }
            } else if (!activeAccount) {
                await msalInstance.loginRedirect({ scopes });
            }
        };
    }
}